import { InstalledPackage, SoftwareEnvironmentBuildSchema } from "@/api-client";
import { useSoftwareSpec } from "@/crud/software/hooks";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import React from "react";
import { canonicalizeName, unifyPackageSpec } from "./utils";

export type BuildPackageRow = InstalledPackage & {
  clientVersion: string | undefined;
};

const BuildColumns: GridColDef<BuildPackageRow>[] = [
  {
    field: "name",
    headerName: "Package Name",
    flex: 1,
    valueGetter: (params) => {
      return params.row.condaName || params.row.name || "";
    },
  },
  { field: "version", headerName: "Cluster Version", flex: 1 },
  {
    field: "clientVersion",
    headerName: "Client Version",
    flex: 1,
    valueGetter: (params) => params.row.clientVersion || "",
  },
  { field: "source", headerName: "Source", flex: 1 },
  {
    field: "channel",
    headerName: "Channel",
    flex: 1,
    valueGetter: (params) => {
      return params.row.channel || "";
    },
  },
];

type InstalledSoftwareTableProps = {
  build: SoftwareEnvironmentBuildSchema;
};

export const InstalledSoftwareTable = ({
  build,
}: InstalledSoftwareTableProps): React.ReactElement => {
  const packages = build?.packages || [];
  const { data: spec } = useSoftwareSpec(build?.specId);
  const unifiedSpecPackages = spec ? unifyPackageSpec(spec) : [];

  // Transform packages to BuildPackageRow with clientVersion
  const buildPackageRows: BuildPackageRow[] = packages.map((pkg) => {
    const match = unifiedSpecPackages.find((specPkg) => {
      const specName =
        specPkg.source === "conda" ? specPkg.condaName : specPkg.name;
      const pkgName = pkg.source === "conda" ? pkg.condaName : pkg.name;
      return (
        specName &&
        pkgName &&
        specPkg.source === pkg.source &&
        canonicalizeName(specName) === canonicalizeName(pkgName)
      );
    });

    return {
      ...pkg,
      clientVersion: match?.clientVersion,
    };
  });

  return (
    <DataGridPro<BuildPackageRow>
      sx={{ border: "unset" }}
      initialState={{
        sorting: {
          sortModel: [{ field: "name", sort: "asc" }],
        },
      }}
      columns={BuildColumns}
      rows={buildPackageRows}
    />
  );
};
